import {
  AddressParametersDto,
  ClientParametersDto,
  OfferAzureSearch,
} from "library/types/types";
import { ClientSearchResult } from "state/queries/useClientSearch";

/** Editable fields of a payer that differs from the client (invoice and group invoice dialogs) */
export interface PayerFormData {
  payerType: "company" | "person";
  payerName: string;
  payerFirstName: string;
  payerLastName: string;
  payerCode: string;
  resident: boolean;
  payerEmail: string;
  payerPhoneNumber: string;
  address: string;
  city: string;
  cityAddress: string;
  postIndex: string;
  isExistingInGOS: boolean;
}

export const EMPTY_PAYER_FORM: PayerFormData = {
  payerType: "company",
  payerName: "",
  payerFirstName: "",
  payerLastName: "",
  payerCode: "",
  resident: true,
  payerEmail: "",
  payerPhoneNumber: "",
  address: "",
  city: "",
  cityAddress: "",
  postIndex: "",
  isExistingInGOS: false,
};

const emptyAddress = (): AddressParametersDto => ({
  AddressString: null,
  Country: null,
  City: null,
  Suburb: null,
  Village: null,
  Street: null,
  House: null,
  Flat: null,
  Index: null,
  Phone: null,
  Email: null,
  MobilePhone: null,
});

/** Payer fields filled from a GOS (CRM) partner found by the search dialog */
export const payerFormFromGOS = (client: ClientSearchResult): PayerFormData => {
  const isCompany = client.isCompany;
  const form: PayerFormData = {
    ...EMPTY_PAYER_FORM,
    payerType: isCompany ? "company" : "person",
    isExistingInGOS: true,
    payerName: isCompany ? client.partnerName || "" : "",
    payerFirstName: isCompany ? "" : client.firstName || "",
    payerLastName: isCompany ? "" : client.lastName || "",
    // company register number (else partner number) for companies, personal identification number for persons
    payerCode: isCompany
      ? client.companyRegisterNumber || client.partnerNumber || ""
      : client.personIdentificationNumber || "",
    resident: client.isResident ?? EMPTY_PAYER_FORM.resident,
  };

  const addr = client.partnerAddress;
  if (addr) {
    form.address = [addr.street, addr.house, addr.buildingFloorDoor]
      .filter(Boolean)
      .join(", ");
    form.city = addr.city || "";
    form.cityAddress = addr.street || "";
    form.postIndex = addr.zipCode || "";
  }

  form.payerEmail = client.partnerMail?.emailAddress || "";

  const phone = client.partnerPhone;
  if (phone) {
    form.payerPhoneNumber = [
      phone.phoneCountry ? `+${phone.phoneCountry}` : "",
      phone.phoneArea,
      phone.phoneNumber,
      phone.phoneExtension ? `ext ${phone.phoneExtension}` : "",
    ]
      .filter(Boolean)
      .join(" ");
  }
  return form;
};

/** Payer sent to the backend (ClientParameters) from the "different payer" fields */
export const buildPayerFromForm = (pd: PayerFormData): ClientParametersDto => {
  const isCompany = pd.payerType === "company";
  return {
    IsExistingInGOS: pd.isExistingInGOS,
    IsCompany: isCompany,
    Code: pd.payerCode.trim() || null,
    Name: isCompany
      ? pd.payerName.trim() || null
      : `${pd.payerFirstName.trim()} ${pd.payerLastName.trim()}`.trim() || null,
    FirstName: isCompany ? null : pd.payerFirstName.trim() || null,
    LastName: isCompany ? null : pd.payerLastName.trim() || null,
    Address: {
      ...emptyAddress(),
      AddressString: pd.address.trim() || null,
      City: pd.city.trim() || null,
      Street: pd.cityAddress.trim() || null,
      Index: pd.postIndex.trim() || null,
      Phone: pd.payerPhoneNumber.trim() || null,
      Email: pd.payerEmail.trim() || null,
    },
    IsResident: pd.resident,
  };
};

/** "Same as client": the invoice payer is a copy of the client data of the offer */
export const copyClientAsPayer = (
  client: ClientParametersDto,
): ClientParametersDto => JSON.parse(JSON.stringify(client));

export const getPayerDisplayName = (
  payer: ClientParametersDto | null | undefined,
): string => {
  if (!payer) return "";
  if (payer.IsCompany) return payer.Name || "";
  return (
    `${payer.FirstName || ""} ${payer.LastName || ""}`.trim() || payer.Name || ""
  );
};

/**
 * Salutation ({Prefix} {ClientName}) and recipient of an invoice e-mail: the payer stored on the invoice;
 * invoices issued before the payer was stored on the invoice fall back to the client.
 */
export const getInvoiceEmailAddressee = (
  payer: ClientParametersDto | null | undefined,
  client: { isPerson: boolean; name: string; email: string },
): { Prefix: string; ClientName: string; email: string } => {
  if (!payer) {
    return {
      Prefix: client.isPerson ? "Cien." : "",
      ClientName: client.name,
      email: client.email,
    };
  }
  return {
    Prefix: payer.IsCompany === false ? "Cien." : "",
    ClientName: getPayerDisplayName(payer),
    email: payer.Address?.Email || "",
  };
};

const isBlank = (value: string | null | undefined): boolean =>
  !value || !value.trim();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The payer e-mail is mandatory and must look like an e-mail address */
export const isPayerEmailValid = (
  payer: ClientParametersDto | null | undefined,
): boolean => EMAIL_REGEX.test((payer?.Address?.Email || "").trim());

/**
 * A client / payer chosen from the offered options without an e-mail: the e-mail entered in the dialog is added to
 * the invoice payer (a copy; the client of the offer is not changed)
 */
export const completePayerEmail = (
  payer: ClientParametersDto,
  email: string,
): ClientParametersDto => {
  if (!isBlank(payer.Address?.Email)) return payer;
  return {
    ...payer,
    Address: { ...(payer.Address || emptyAddress()), Email: email.trim() || null },
  };
};

/**
 * Translation keys of the payer data the backend requires (PayerValidator): code; name for companies,
 * first and last name for persons; address; e-mail. Empty when the payer is complete.
 */
export const getPayerMissingFieldKeys = (
  payer: ClientParametersDto | null | undefined,
): string[] => {
  if (!payer) return ["qtg.invoicePayer.title"];
  const missing: string[] = [];
  if (payer.IsCompany === true && isBlank(payer.Name)) {
    missing.push("qtg.proposal.payerData.payerName");
  }
  if (payer.IsCompany === false) {
    if (isBlank(payer.FirstName))
      missing.push("qtg.proposal.payerData.payerFirstName");
    if (isBlank(payer.LastName))
      missing.push("qtg.proposal.payerData.payerLastName");
  }
  if (isBlank(payer.Code)) missing.push("qtg.proposal.payerData.payerCode");
  if (isBlank(payer.Address?.AddressString)) {
    missing.push("qtg.proposal.payerData.address");
  }
  if (isBlank(payer.Address?.Email)) {
    missing.push("qtg.proposal.payerData.payerEmail");
  }
  return missing;
};

/**
 * Validation message of the invoice payer (translation key + default text), or null when the payer is valid:
 * missing mandatory data first, then an invalid e-mail
 */
export const getPayerValidationError = (
  payer: ClientParametersDto | null | undefined,
  t: (key: string, options?: any) => string,
): string | null => {
  const missing = getPayerMissingFieldKeys(payer);
  if (missing.length > 0) {
    return t("qtg.invoicePayer.validation.incomplete", {
      fields: missing.map((key) => t(key)).join(", "),
      defaultValue: "Payer data is incomplete: {{fields}}",
    });
  }
  if (!isPayerEmailValid(payer)) {
    return t("qtg.invoicePayer.validation.emailInvalid", "Payer e-mail is not valid");
  }
  return null;
};

/** Field groups of a list row (Azure Search) holding a party: the client or the payer of an invoice */
export type SearchRowPartyPrefix =
  | "Client"
  | "InvoiceMtplPayer"
  | "InvoiceCascoPayer"
  | "GroupInvoicePayer";

/** The party stored under the given prefix of a list row, or null when the row has none */
export const partyFromSearchRow = (
  row: OfferAzureSearch,
  prefix: SearchRowPartyPrefix,
): ClientParametersDto | null => {
  const r = row as unknown as Record<string, any>;
  const code = r[`${prefix}Code`] as string | null | undefined;
  const name = r[`${prefix}Name`] as string | null | undefined;
  const firstName = r[`${prefix}FirstName`] as string | null | undefined;
  const lastName = r[`${prefix}LastName`] as string | null | undefined;
  if (!code && !name && !firstName && !lastName) return null;
  return {
    IsCompany: r[`${prefix}IsCompany`] ?? null,
    IsResident: r[`${prefix}IsResident`] ?? null,
    Code: code || null,
    Name: name || null,
    FirstName: firstName || null,
    LastName: lastName || null,
    Address: {
      ...emptyAddress(),
      AddressString: r[`${prefix}AddressAddressString`] || null,
      City: r[`${prefix}AddressCity`] || null,
      Email: r[`${prefix}AddressEmail`] || null,
      Phone: r[`${prefix}AddressPhone`] || null,
    },
  };
};

/**
 * Payer of the invoice a list row is settled through: the group invoice payer for a grouped offer, else the payer of
 * its line invoice (MTPL, else CASCO); the client for invoices issued before the payer was stored on the invoice.
 */
export const getSearchRowInvoicePayer = (
  row: OfferAzureSearch,
  isGroupInvoice: boolean,
): { code: string; name: string } => {
  const payer =
    (isGroupInvoice ? partyFromSearchRow(row, "GroupInvoicePayer") : null) ||
    partyFromSearchRow(row, "InvoiceMtplPayer") ||
    partyFromSearchRow(row, "InvoiceCascoPayer") ||
    partyFromSearchRow(row, "Client");
  return {
    code: payer?.Code || "",
    name: getPayerDisplayName(payer),
  };
};
